import React, {
    useMemo,
    useState,
    useCallback,
    useEffect,
    useRef,
} from "react";
import { FiltersTypes } from "@common/types/filters/general";
import {
    ClassesView,
    ClassesViewSchema,
    ClassesColumn,
} from "@common/types/views/classesView";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { ServerInferResponseBody } from "@ts-rest/core";
import { arrayMove } from "@dnd-kit/sortable";
import { classContract } from "@common/contracts/class.contract";
import { useBusinessEntityIds } from "@hooks/useBusinessEntityIds";
import {
    useClasses,
    useClassStatistics,
    useChangeClassPositionMutation,
} from "@api/class.api";
import { ExpandedState } from "@tanstack/react-table";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { ClassesSettingsHeader } from "./children/ClassesSettingsHeader";
import { ClassesStats } from "./ClassesStats";
import { createClassesViewConfig } from "./classesViewConfig";
import {
    buildClassRows,
    ClassRow,
    ClassStatsMap,
    getClassDefaultExpandedState,
} from "./viewUtils";

type ClassStat = ServerInferResponseBody<
    typeof classContract.getClassStatistics
>[number];

const defaultView: ClassesView = {
    type: ViewType.CLASSES,
    columns: [
        { id: ClassesColumn.NAME },
        { id: ClassesColumn.PERCENTAGE },
        { id: ClassesColumn.TRANSACTIONS },
        { id: ClassesColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: { density: ViewDensity.LG },
};

const viewConfig = createClassesViewConfig();
const ROOT_GROUP_ID = "__root__";

export const ClassesSettings: React.FC = () => {
    const businessEntityIds = useBusinessEntityIds();
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<
        Pick<FiltersTypes, "end" | "start" | "entityIds" | "classes">
    >({ entityIds: businessEntityIds });

    const { classesWithChildren } = useClasses();
    const changeClassPosition = useChangeClassPositionMutation();
    const stats = useClassStatistics(filters);

    const statsMap = useMemo(
        () =>
            new Map<string, ClassStatsMap>(
                (stats.data?.body ?? []).map((stat) => [
                    stat.id,
                    { count: stat.count },
                ]),
            ),
        [stats.data?.body],
    );

    const classesStatsMap = useMemo(
        () =>
            new Map<string, ClassStat>(
                (stats.data?.body ?? []).map((stat) => [stat.id, stat]),
            ),
        [stats.data?.body],
    );

    const total = useMemo(
        () =>
            (stats.data?.body ?? []).reduce(
                (acc, stat) =>
                    !stat.parentClassId
                        ? acc + (stat.groupCount || stat.count)
                        : acc,
                0,
            ),
        [stats.data?.body],
    );

    const rows = useMemo(
        () =>
            buildClassRows(classesWithChildren, {
                search,
                filters,
                statsMap,
                total,
            }),
        [classesWithChildren, search, filters, statsMap, total],
    );

    const [cachedView, setCachedView] = useWorkspaceUserCache<ClassesView>(
        WORKSPACE_USER_CACHE_KEYS.CLASSES_PAGE_VIEW_CACHE_KEY,
        ClassesViewSchema,
    );

    const [view, setView] = useState<ClassesView>(cachedView ?? defaultView);
    const handleViewChange = useCallback(
        (newView: View) => {
            setView(newView as ClassesView);
            setCachedView(newView as ClassesView);
        },
        [setCachedView],
    );

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);
    const hasInitializedExpandedRef = useRef(false);

    useEffect(() => {
        if (hasInitializedExpandedRef.current || rows.length === 0) {
            return;
        }

        hasInitializedExpandedRef.current = true;
        setExpanded(getClassDefaultExpandedState(rows));
    }, [rows]);

    const handleSubclassAdded = useCallback((parentId: string) => {
        setExpanded((prev) =>
            prev === true ? prev : { ...prev, [parentId]: true },
        );
    }, []);

    const handleToggleClass = useCallback(
        (id: string) => {
            const parentClassId =
                rows.find((row) => row.id === id)?.parentId ?? id;
            if (filters.classes?.includes(parentClassId)) {
                setFilters((prev) => ({ ...prev, classes: undefined }));
            } else {
                setFilters((prev) => ({ ...prev, classes: [parentClassId] }));
            }
        },
        [rows, filters.classes],
    );

    const tableMeta = useMemo(
        () => ({
            entityIds: filters.entityIds,
            onSubclassAdded: handleSubclassAdded,
        }),
        [filters.entityIds, handleSubclassAdded],
    );

    const siblingIdsByGroup = useMemo(() => {
        const map = new Map<string, string[]>();

        map.set(
            ROOT_GROUP_ID,
            rows.filter((row) => row.parentId === null).map((row) => row.id),
        );

        // Walk the full tree so drag-reorder works within a sibling group at
        // any depth (a drag always stays within the same parent). At depth 2
        // only roots have children, so this matches the previous behavior.
        const collectGroups = (nodes: ClassRow[]) => {
            for (const node of nodes) {
                if (!node.children?.length) {
                    continue;
                }
                map.set(
                    node.id,
                    node.children.map((childRow) => childRow.id),
                );
                collectGroups(node.children);
            }
        };
        collectGroups(rows);

        return map;
    }, [rows]);

    // Reorder computes the new `order` against the sibling set derived from the
    // currently rendered rows. While a search term or class filter is active
    // those rows are a filtered subset, so a drag could send an `order` that is
    // wrong relative to the full sibling list. Disable dragging in that case.
    const isRowDragDisabled =
        search.length > 0 || (filters.classes?.length ?? 0) > 0;

    const canDragRow = useCallback(
        () => !changeClassPosition.isPending && !isRowDragDisabled,
        [changeClassPosition.isPending, isRowDragDisabled],
    );

    const getRowDragGroupId = useCallback(
        (row: ClassRow) => row.parentId ?? ROOT_GROUP_ID,
        [],
    );

    const handleRowDrop = useCallback(
        async ({
            activeRow,
            overRow,
        }: {
            activeRow: ClassRow;
            overRow: ClassRow;
        }) => {
            const activeGroupId = activeRow.parentId ?? ROOT_GROUP_ID;
            const overGroupId = overRow.parentId ?? ROOT_GROUP_ID;
            if (activeGroupId !== overGroupId) {
                return;
            }

            const siblingIds = siblingIdsByGroup.get(activeGroupId);
            if (!siblingIds || siblingIds.length <= 1) {
                return;
            }

            const sourceIndex = siblingIds.indexOf(activeRow.id);
            const targetIndex = siblingIds.indexOf(overRow.id);
            if (
                sourceIndex === -1 ||
                targetIndex === -1 ||
                sourceIndex === targetIndex
            ) {
                return;
            }

            const reorderedIds = arrayMove(
                siblingIds,
                sourceIndex,
                targetIndex,
            );
            const newOrder = reorderedIds.indexOf(activeRow.id);
            if (newOrder === sourceIndex) {
                return;
            }

            await changeClassPosition.mutateAsync({
                params: {
                    id: activeRow.id,
                },
                body: {
                    parentClassId: activeRow.parentId,
                    order: newOrder,
                    mergingStrategy: "child",
                },
            });
        },
        [changeClassPosition, siblingIdsByGroup],
    );

    const rowDragAndDropConfig = useMemo(
        () => ({
            canDragRow,
            getRowDragGroupId,
            onRowDrop: handleRowDrop,
            dragHandleColumnId: ClassesColumn.ACTIONS,
        }),
        [canDragRow, getRowDragGroupId, handleRowDrop],
    );

    const displaySegmentBar = useMemo(
        () => rows.length > 0 && total > 0,
        [rows.length, total],
    );

    // Flatten the row tree to every visible node (any depth) so the stats bar
    // can tell which classes are currently shown. At depth 2 this is roots +
    // their direct children, matching the previous behavior.
    const visibleClasses = useMemo(() => {
        const flat: ClassRow[] = [];
        const walk = (nodes: ClassRow[]) => {
            for (const node of nodes) {
                flat.push(node);
                if (node.children?.length) {
                    walk(node.children);
                }
            }
        };
        walk(rows);
        return flat;
    }, [rows]);

    return (
        <main>
            <ClassesSettingsHeader
                expanded={expanded}
                tableRef={tableRef}
                search={search}
                setSearch={setSearch}
                filters={filters}
                onFiltersChange={setFilters}
            />

            {displaySegmentBar && (
                <ClassesStats
                    statsMap={classesStatsMap}
                    stats={stats.data?.body ?? []}
                    total={total}
                    classes={visibleClasses}
                    toggleClass={handleToggleClass}
                />
            )}

            <ViewTable
                ref={tableRef}
                data={rows}
                view={view}
                config={viewConfig}
                onViewChange={handleViewChange}
                expanded={expanded}
                onExpandedChange={setExpanded}
                rowBorders={false}
                isParentSelectable={false}
                fixedTopOffsetPx
                // The stats bar renders conditionally above the table and
                // toggles at runtime as data/filters change — a position-only
                // shift the auto top-offset's ResizeObserver won't catch, so
                // force a re-measure whenever it appears/disappears.
                recomputeTopOffsetOn={[displaySegmentBar]}
                tableMeta={tableMeta}
                rowDragAndDrop={rowDragAndDropConfig}
                nestingIndentPx={16}
            />
        </main>
    );
};
